import { MessageDefinition, NumberUtils, REPORTED_EXCEPTION, isEmpty, showMessageBox, styled, webapi } from 'ps-gui';
import { UnlockDialog } from './unlockdialog';
import CorrectionInput, { Bubble } from './correctioninput';
import Immutable from 'immutable';
import memoizeOne from 'memoize-one';
import { getLookup } from '../../data/lookup';
import { getFormItemLabel } from '../../data/phrases';

const formatAmount = NumberUtils.getFixedDecimalFormatNumber(2);
const formatShare = NumberUtils.getFixedDecimalFormatNumber(4);
const formatNumber = NumberUtils.formatNumber;


export const CORRECTION_DIALOG_PATH = ['tmp', 'correctionDialog'];

export const searchTabCustomResultProps = {
	cellRendererMap: {
		digital_sale_income: (cell, row, colIndex) => {
			return formatAmount(cell);
		},
		physical_sale_income: (cell, row, colIndex) => {
			return formatAmount(cell);
		},
		attendance: (cell, row, colIndex) => {
			return formatNumber(cell);
		},
		correction: (cell, row, colIndex, ctx) => {
			return (
				<CorrectionInput
					value={cell}
					noButton={isEmpty(cell)}
					onMouseDown={async e => {
						const type = row.get('type');
						const id = row.get('id');
						const proc = type === 'ca' ? 'getCinemaAttendanceAttachments' : 'getBroadcastAttachments';

						let attachments = await webapi.execute('/api/proc/api_affirmations/' + proc, {
							id
						});
						if (attachments === REPORTED_EXCEPTION) {
							return;
						}
						attachments = attachments || [];
						showCorrectionDialog(ctx, cell, Immutable.fromJS(attachments), {
							readOnly: true
						});
					}}
				/>
			);
		},
		note: (cell, row, colIndex, ctx) => {
			const empty = isEmpty(cell);
			return (
				<Bubble
					text={getFormItemLabel('note')}
					value={cell}
					noButton={empty}
					onMouseDown={e => {
						if (!empty) {
							showMessageBox(cell);
						}
					}}
				/>
			);
		}
	},
	withNewEntityButton: false,
	defaultColumnWidths: {
		'country': '100px',
		correction: '110px',
		note: '110px',
		'share_pct': '80px',
		'isan': '100px',
		production_year: '120px',
		currency: '80px',
		'attendance': '120px',
		'status': '180px',
		'release_date': '110px',
		runtime: '100px',
		digital_sale_income: '120px',
		physical_sale_income: '120px',
		broadcast_start_time: '120px',
		affirmation_recording_type: '140px',
		channel: '140px',
	},
	columnAlign: {
		attendance: 'right',
		production_year: 'right',
		share_pct: 'right',
		runtime: 'right',
		note: 'center',
		correction: 'center',
		digital_sale_income: 'right',
		physical_sale_income: 'right',
		broadcast_start_time: 'right',
	},
	columnFilters: {
		'correction': {
			type: 'lookup',
			lookup: 'ynBoolean'
		},
		'currency': {
			type: 'lookup',
			lookup: 'affirmationCurrency'
		},
		'note': {
			type: 'lookup',
			lookup: 'ynBoolean'
		},
		'channel': {
			type: 'lookup',
			lookup: 'affirmationChannel'
		},
		'affirmation_recording_type': {
			type: 'lookup',
			lookup: 'affirmationRecordingType'
		},
		'runtime': {
			type: 'number',
			inputSize: 4,
		},
		'attendance': {
			type: 'number',
			inputSize: 8,
		},
		'share_pct': {
			type: 'number',
			inputSize: 8,
			maxDecimals: 4
		},
		'digital_sale_income': {
			type: 'number',
			inputSize: 8,
		},
		'physical_sale_income': {
			type: 'number',
			inputSize: 8,
		},
		'production_year': {
			type: 'number',
			inputSize: 4,
			groupless: true
		},
		'recording_name': {
			type: 'text'
		},
		'original_recording_name': {
			type: 'text'
		},
	}
};

export function getCorrectionMessageDefinition() {
	return MessageDefinition(ctx => {
		const dlgPath = CORRECTION_DIALOG_PATH;
		if (!ctx.getRootProperty(dlgPath)) {
			return null;
		}
		const data = ctx.getRootProperty(dlgPath);
		return (
			<UnlockDialog
				onCancel={() => {
					data.get('callback')(null);
				}}
				onOk={() => {
					data.get('callback')(data);
				}}
				data={ctx.getRootProperty(dlgPath)}
				setData={data => ctx.setRootProperty(dlgPath, data)}
				readOnly={data.get('readOnly')}
				noAttachments={data.get('noAttachments')}
				noSubtext={data.get('noSubtext')}
				header={data.get('header')}
				optional={data.get('optional')}
			/>
		);
	});
}


export function renderTotalTable(stats) {
	return (
		<table>
			<tbody>
				<tr><td>{getFormItemLabel('affSumTotalRuntime')}</td><td className='value'>{NumberUtils.formatNumber(stats.broadcastRuntime)}</td></tr>
				<tr><td>{getFormItemLabel('affSumTotalPhysicalSaleIncome')}</td><td className='value'>{formatSaleStats(stats.physicalSales)}</td></tr>
				<tr><td>{getFormItemLabel('affSumTotalDigitalSaleIncome')}</td><td className='value'>{formatSaleStats(stats.digitalSales)}</td></tr>
				<tr><td>{getFormItemLabel('affSumTotalAttendance')}</td><td className='value'>{NumberUtils.formatNumber(stats.cinemaAttendance)}</td></tr>
			</tbody>
		</table>
	);
}


export function formatSaleStats(sales) {
	if (sales.length === 0) {
		return null;
	}
	return sales.map((item, index) => {
		return (
			<div className="amounts" key={index}>
				<span className="amount">{formatAmount(item.value)}</span><span className='currency'>{item.currency}</span>
			</div>
		);
	});
}

export async function showCorrectionDialog(ctx, comments, attachments, pars) {
	pars = pars || {};
	const dlgPath = CORRECTION_DIALOG_PATH;
	let _resolve;
	const res = new Promise(function(resolve) {
		_resolve = resolve;
	});
	const callback = function(res) {
		ctx.setRootProperty(dlgPath, null);
		_resolve(res);
	};
	ctx.setRootProperty(dlgPath, Immutable.Map({
		comments,
		attachments,
		readOnly: pars.readOnly,
		noAttachments: pars.noAttachments,
		noSubtext: pars.noSubtext,
		header: pars.header,
		optional: pars.optional,
		callback
	}));
	return res;
}

export const DisclaimerWrapper = styled.div`
	max-width: 80vw;
	max-height: calc( 80vh - 80px );
	overflow: auto;
	/* padding-right: 16px; */
	margin: ${props => props.margin};

	li {
		text-align: justify;
	}

	ul.main {
		> li {
			margin-bottom: 16px;
		}
	}

	table {
		border-collapse: collapse;
		width: 100%;
		border: 1px solid ${props => props.theme.textColor + '77'};
		margin-top: 8px;
		td {
			border: 1px solid ${props => props.theme.textColor + '77'};
			padding: 4px 8px;
			white-space: pre;

			&.value {
				text-align: right;

				.currency {
					opacity: 0.6;
					padding-left: 8px;
				}
			}
		}
	}
`;


const getSaleCurrencies = memoizeOne(() => {
	return getLookup('affirmationCurrency').getItems().map(item => item.get('key')).toArray();
});

export function totalSaleMap2Array(map) {
	let res = [];
	const currs = getSaleCurrencies();
	currs.forEach(currency => {
		const value = map[currency];
		if (value) {
			res.push({
				currency,
				value
			});
		}
	});
	return res;
}


